import { Form, notification, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { PatchedPortfolioRequestDefaultPermission } from 'src/api-sdk';
import PermissionOptionSelect from 'src/components/Permissions/components/PermissionOptionSelect';
import { useUpdatePortfolio } from 'src/hooks/portfolio/useUpdatePortfolio';
import { usePortfolioContext } from 'src/util/context/PortfolioContext';
import { QUERIES } from 'src/util/globals';
import { useAPI } from 'src/util/use-api';

export const PortfolioDefaultPermission = () => {
  const { t } = useTranslation('permissions');
  const { portfolioId } = usePortfolioContext();
  const { API } = useAPI();
  const client = useQueryClient();

  const { data: portfolio, isPending } = useQuery({
    queryKey: [QUERIES.PORTFOLIO, portfolioId],
    queryFn: async () => {
      if (!portfolioId || !API) return null;
      const response = await API.portfoliosAPI.portfoliosRetrieve({
        portfolioId,
      });
      return response.data;
    },
    enabled: Boolean(portfolioId && API),
  });

  const { mutateAsync: updatePortfolio, isPending: isSaving } =
    useUpdatePortfolio({
      onSuccess: () => {
        client.invalidateQueries({
          queryKey: [QUERIES.PORTFOLIO, portfolioId],
        });
        client.invalidateQueries({
          queryKey: [QUERIES.PORTFOLIOS],
        });
        notification.success({
          message: t('Organisation-wide access updated'),
        });
      },
    });

  const handleChange = useCallback(
    async (value: string | null) => {
      if (!portfolioId) return;
      try {
        await updatePortfolio({
          portfolioId,
          data: {
            default_permission:
              (value as PatchedPortfolioRequestDefaultPermission) ?? null,
          },
        });
      } catch {
        // surfaced by global API error interceptor
      }
    },
    [portfolioId, updatePortfolio]
  );

  if (isPending) {
    return <Skeleton.Input active size="small" />;
  }

  return (
    <div className="mb-4 p-3 border rounded">
      <Typography.Title level={5} className="!mb-1">
        {t('Organisation-wide access')}
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="!mb-2 text-xs">
        {t(
          'When set, every member of your organisation automatically gets this permission for the portfolio.'
        )}
      </Typography.Paragraph>
      <Form layout="vertical">
        <Form.Item className="!mb-0">
          <PermissionOptionSelect
            entity="portfolio"
            allowClear
            disabled={isSaving}
            placeholder={t('Not shared with organisation')}
            value={portfolio?.default_permission ?? undefined}
            onChange={handleChange}
          />
        </Form.Item>
      </Form>
    </div>
  );
};
